import React, { useEffect, useReducer, useState } from "react";
import DashboardContainer from "../../components/dashboard-container";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import SalesByCategoryList from "../../components/sales-by-category-list";
import SalesByCategoryModal from "../../components/sales-by-category-modal";
import CustomPagination from "../../components/pagination";
import { salesByCategoryService } from "../../utils/services/salesByCategoryService";
import buildQuery from "../../utils/query-builder";
import { filterReducer } from "../../utils/filterConfig";
import { salesByCategoryFilterConfig } from "../../utils/filter-config";
import ImportIcon from "../../globalStyles/icons/import-icon";
import { useNavigate } from "react-router-dom";

export default function SalesByCategory() {
  const clientId = localStorage.getItem("client_id");

  const [filterData, dispatch] = useReducer(
    filterReducer,
    salesByCategoryFilterConfig,
  );
  const [salesEntries, setSalesEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [dataCount, setDataCount] = useState(0);
  const [regenerating, setRegenerating] = useState(false);
  const navigate = useNavigate();

  const fetchSalesEntries = async () => {
    setLoading(true);
    try {
      const query = buildQuery(filterData, page);
      const response = await salesByCategoryService.fetch(
        searchValue,
        query,
        clientId,
      );
      setSalesEntries(response?.data || []);
      setDataCount(response?.total || 0);
    } catch (error) {
      console.error("Fetch sales entries failed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesEntries();
  }, [page, filterData, searchValue]);

  const handleFilterChange = (newFilter) => {
    dispatch({ type: "SET_FILTER", payload: newFilter });
    setPage(0);
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await salesByCategoryService.regenerate(clientId);
      await fetchSalesEntries();
    } catch (error) {
      console.error("Regenerate failed:", error);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <>
      <DashboardContainer
        searchBarPlaceholder="Search by Category"
        onChangeFilter={handleFilterChange}
        filterData={filterData}
        fields={filterData.columns}
        hideFilterClient
        onSearchData={(val) => {
          setSearchValue(val);
          setPage(0);
        }}
        onClickAddButton={() => {
          setSelectedEntry(null);
          setOpenModal(true);
        }}
        hideUploadButton
        header="Sales By Category"
        title="+ Add Entry"
        onClickRefresh={() => fetchSalesEntries()}
        headerButtons={
          <>
            <Button
              size="small"
              style={{
                textTransform: "none",
                marginRight: 10,
              }}
              variant="contained"
              color="primary"
              disabled={regenerating}
              onClick={handleRegenerate}
            >
              {regenerating ? (
                <CircularProgress size={14} sx={{ mr: 1 }} color="inherit" />
              ) : null}
              <Typography
                fontSize="14px"
                fontWeight={500}
                sx={{
                  color: "#FFF",
                  marginLeft: regenerating ? 1 : 0,
                }}
                fontFamily={"poppins"}
              >
                {regenerating ? "Regenerating..." : "Regenerate"}
              </Typography>
            </Button>
            {!clientId ? (
              <></>
            ) : (
              <Button
                size="small"
                style={{
                  textTransform: "none",
                  marginRight: 10,
                }}
                variant="contained"
                color="primary"
                onClick={() => {
                  navigate("/sales-by-category-import");
                }}
              >
                <ImportIcon fill={"#ffff"} />
                <Typography
                  fontSize="14px"
                  fontWeight={500}
                  sx={{
                    color: "#FFF",
                    marginLeft: 1,
                  }}
                  fontFamily={"poppins"}
                >
                  Import
                </Typography>
              </Button>
            )}
          </>
        }
      >
        <SalesByCategoryModal
          open={openModal}
          selectedEntry={selectedEntry}
          handleClose={() => {
            setOpenModal(false);
            setSelectedEntry(null);
          }}
          onAddData={fetchSalesEntries}
          clientId={clientId}
        />

        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 9999,
              color: "white",
            }}
          >
            <CircularProgress color="inherit" />
          </Box>
        )}

        <SalesByCategoryList
          data={salesEntries}
          onEditData={(entry) => {
            setSelectedEntry(entry);
            setOpenModal(true);
          }}
          onDeleteData={fetchSalesEntries}
          filterData={filterData}
          onChangeFilter={handleFilterChange}
          clientId={clientId}
        />

        <CustomPagination
          dataCount={dataCount}
          rowsPerPage={filterData.limit}
          page={page}
          setPage={setPage}
          setRowsPerPage={(val) =>
            dispatch({ type: "UPDATE_LIMIT", payload: val })
          }
        />
      </DashboardContainer>
    </>
  );
}
